import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { PlatformContactsService } from '../../services/platform-contacts.service';
import { NotificationService } from '../../services/notification.service';
import { TECHNICAL_JARGON } from '../../utils/friendly-error';
import { KuboComponent, KuboRecommendation } from '../ui/kubo.component';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, KuboComponent],
  template: `
    <main class="login-page" [style.--login-bg]="loginBgUrl">
      <div class="ambient ambient-one" aria-hidden="true"></div>
      <div class="ambient ambient-two" aria-hidden="true"></div>
      <div class="particle-field" aria-hidden="true">
        <span></span><span></span><span></span><span></span><span></span><span></span>
      </div>

      <section class="visual-panel" aria-label="TuSala, gestión empresarial de espacios">
        <div class="visual-copy">
          <span class="visual-kicker"><i class="bi bi-stars"></i> Plataforma para propietarios</span>
          <h2>Administra tus espacios con claridad y control.</h2>
          <p>Centraliza reservas, clientes y operación diaria desde un solo lugar.</p>
        </div>

        <div class="benefit-card benefit-spaces">
          <span class="benefit-icon"><i class="bi bi-buildings"></i></span>
          <div><strong>Gestiona tus espacios</strong><small>Publica, edita y controla tus salas.</small></div>
        </div>
        <div class="benefit-card benefit-growth">
          <span class="benefit-icon violet"><i class="bi bi-graph-up-arrow"></i></span>
          <div><strong>Haz crecer tu negocio</strong><small>Convierte disponibilidad en reservas.</small></div>
        </div>
        <div class="benefit-card benefit-security">
          <span class="benefit-icon cyan"><i class="bi bi-shield-check"></i></span>
          <div><strong>Seguro y conectado</strong><small>Tu operación protegida en tiempo real.</small></div>
        </div>
      </section>

      <section class="access-panel">
        <div class="login-card" *ngIf="mode !== 'reset'">
          <header class="login-header">
            <img src="TuSala_LogoSinFondo.png" alt="TuSala" class="login-logo" />
            <span class="admin-badge"><i class="bi bi-shield-lock"></i> Panel Administrativo</span>
            <h1>{{ mode === 'forgot' ? 'Recupera tu contraseña' : mode === '2fa' ? 'Verificación en dos pasos' : 'Bienvenido nuevamente' }}</h1>
            <p>{{ mode === 'forgot' ? 'Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.' : mode === '2fa' ? 'Tu cuenta tiene verificación en dos pasos activada. Ingresa el código para continuar.' : 'Accede para administrar tus espacios publicados en TuSala.' }}</p>
          </header>

          <form *ngIf="mode === 'login'" (ngSubmit)="onLogin()" class="login-form" novalidate>
            <div class="form-group">
              <label for="email">Correo electrónico</label>
              <div class="input-shell" [class.has-error]="error">
                <i class="bi bi-envelope input-icon" aria-hidden="true"></i>
                <input
                  type="email"
                  id="email"
                  [(ngModel)]="email"
                  (ngModelChange)="emailError = ''"
                  name="email"
                  required
                  autocomplete="email"
                  inputmode="email"
                  class="form-control"
                  [class.error]="error"
                  placeholder="Ingresa tu correo electrónico"
                  maxlength="100"
                  data-no-limit-hint
                >
              </div>
              <small *ngIf="emailError" class="field-validation-message" role="alert">{{ emailError }}</small>
            </div>

            <div class="form-group">
              <label for="password">Contraseña</label>
              <div class="input-shell password-field" [class.has-error]="error">
                <i class="bi bi-lock input-icon" aria-hidden="true"></i>
                <input
                  [type]="showPassword ? 'text' : 'password'"
                  id="password"
                  [(ngModel)]="password"
                  (ngModelChange)="passwordError = ''"
                  name="password"
                  required
                  autocomplete="current-password"
                  class="form-control"
                  [class.error]="error"
                  placeholder="Ingresa tu contraseña"
                  maxlength="200"
                  data-no-limit-hint
                >
                <button type="button" class="password-toggle" (click)="showPassword = !showPassword"
                  [attr.aria-label]="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'">
                  <i [class]="showPassword ? 'bi bi-eye' : 'bi bi-eye-slash'" aria-hidden="true"></i>
                </button>
              </div>
              <small *ngIf="passwordError" class="field-validation-message" role="alert">{{ passwordError }}</small>
            </div>

            <div class="login-options">
              <label class="remember-option" for="adminRememberMe">
                <input id="adminRememberMe" type="checkbox" name="rememberMe" [(ngModel)]="rememberMe">
                <span class="remember-check" aria-hidden="true"><i class="bi bi-check-lg"></i></span>
                <span>Recordarme</span>
              </label>
              <button type="button" class="link-btn" (click)="goForgot()">¿Olvidaste tu contraseña?</button>
            </div>

            <div *ngIf="error" class="error-message" role="alert">
              <i class="bi bi-exclamation-circle-fill" aria-hidden="true"></i>
              <span>{{ errorMessage }}</span>
            </div>

            <!-- Acceso bloqueado por el plan o por negocio desactivado: en vez
                 de solo el texto del error, contactos reales de la plataforma
                 con acciones de un toque (nunca aparece para super_admin ni
                 negocios exentos: el backend no devuelve estos códigos ahí). -->
            <div *ngIf="accessIssue" class="access-issue" role="note">
              <div class="access-issue-head">
                <i class="bi" [class.bi-credit-card-2-front]="accessIssue === 'SUBSCRIPTION_NOT_ACTIVE'" [class.bi-shop]="accessIssue === 'BUSINESS_DISABLED'" aria-hidden="true"></i>
                <div>
                  <strong>{{ accessIssue === 'SUBSCRIPTION_NOT_ACTIVE' ? 'Activa o renueva tu plan' : 'Tu negocio está desactivado' }}</strong>
                  <p *ngIf="accessIssue === 'SUBSCRIPTION_NOT_ACTIVE'">Tu cuenta no tiene un plan activo. Escríbenos y te enviamos tu enlace de pago para activarla al instante. Tus datos siguen guardados.</p>
                  <p *ngIf="accessIssue === 'BUSINESS_DISABLED'">El acceso a este negocio está pausado. Comunícate con el equipo de TuSala para revisarlo.</p>
                </div>
              </div>
              <div class="access-issue-actions">
                <a *ngIf="accessWhatsappUrl() as url" class="access-btn access-btn-primary" [href]="url" target="_blank" rel="noopener noreferrer">
                  <i class="bi bi-whatsapp" aria-hidden="true"></i> {{ accessIssue === 'SUBSCRIPTION_NOT_ACTIVE' ? 'Solicitar mi enlace de pago' : 'Escribir por WhatsApp' }}
                </a>
                <a class="access-btn" [class.access-btn-primary]="!accessWhatsappUrl()" [href]="accessMailUrl()">
                  <i class="bi bi-envelope" aria-hidden="true"></i> Enviar correo
                </a>
              </div>
              <small class="access-issue-contacts">
                {{ accessSupportEmail() }}<ng-container *ngFor="let phone of accessPhones()"> · {{ phone }}</ng-container>
              </small>
            </div>

            <button type="submit" class="login-btn" [disabled]="loading">
              <span class="button-label">
                <span *ngIf="loading" class="login-spinner" aria-hidden="true"></span>
                {{ loading ? 'Iniciando sesión...' : 'Iniciar sesión' }}
              </span>
              <i *ngIf="!loading" class="bi bi-arrow-right" aria-hidden="true"></i>
            </button>
          </form>

          <div *ngIf="mode === 'login'" class="exclusive-note">
            <i class="bi bi-lock-fill" aria-hidden="true"></i>
            <span>Acceso exclusivo para propietarios registrados.</span>
          </div>

          <button *ngIf="mode === 'login'" type="button" class="support-card" aria-label="Contactar soporte" (click)="contactSupport()">
            <span class="support-icon"><i class="bi bi-headset"></i></span>
            <span class="support-copy"><strong>¿Necesitas ayuda?</strong><small>Nuestro equipo puede ayudarte.</small></span>
            <span class="support-action">Contactar soporte <i class="bi bi-arrow-right"></i></span>
          </button>

          <div *ngIf="mode === '2fa'" class="forgot-block">
            <form (ngSubmit)="submitTwoFactor()" class="login-form" novalidate>
              <div class="form-group">
                <label for="twoFactorCode">Código de verificación</label>
                <div class="input-shell" [class.has-error]="twoFactorError">
                  <i class="bi bi-shield-lock input-icon" aria-hidden="true"></i>
                  <input
                    type="text"
                    id="twoFactorCode"
                    [(ngModel)]="twoFactorCode"
                    name="twoFactorCode"
                    required
                    inputmode="numeric"
                    autocomplete="one-time-code"
                    class="form-control"
                    [class.error]="twoFactorError"
                    placeholder="Código de 6 dígitos"
                    maxlength="6"
                    data-no-limit-hint
                  >
                </div>
                <small style="color:var(--ts-text-sky-250-15);font-size:.76rem">Ingresa el código de tu app de autenticación (Google Authenticator o similar).</small>
              </div>
              <div *ngIf="twoFactorError" class="error-message" role="alert">
                <i class="bi bi-exclamation-circle-fill" aria-hidden="true"></i>
                <span>{{ twoFactorError }}</span>
              </div>
              <button type="submit" class="login-btn" [disabled]="twoFactorLoading || twoFactorCode.length !== 6">
                <span class="button-label">
                  <span *ngIf="twoFactorLoading" class="login-spinner" aria-hidden="true"></span>
                  {{ twoFactorLoading ? 'Verificando...' : 'Verificar e iniciar sesión' }}
                </span>
                <i *ngIf="!twoFactorLoading" class="bi bi-arrow-right" aria-hidden="true"></i>
              </button>
            </form>
            <button type="button" class="link-btn back-link" (click)="goLogin()">Volver al inicio de sesión</button>
          </div>

          <div *ngIf="mode === 'forgot'" class="forgot-block">
            <div *ngIf="!forgotSent; else forgotDone">
              <form (ngSubmit)="submitForgot()" class="login-form" novalidate>
                <div class="form-group">
                  <label for="forgotEmail">Correo electrónico</label>
                  <div class="input-shell" [class.has-error]="forgotError">
                    <i class="bi bi-envelope input-icon" aria-hidden="true"></i>
                    <input
                      type="email"
                      id="forgotEmail"
                      [(ngModel)]="forgotEmail"
                      name="forgotEmail"
                      required
                      autocomplete="email"
                      inputmode="email"
                      class="form-control"
                      [class.error]="forgotError"
                      placeholder="Ingresa tu correo electrónico"
                      maxlength="100"
                      data-no-limit-hint
                    >
                  </div>
                </div>
                <div *ngIf="forgotError" class="error-message" role="alert">
                  <i class="bi bi-exclamation-circle-fill" aria-hidden="true"></i>
                  <span>{{ forgotError }}</span>
                </div>
                <button type="submit" class="login-btn" [disabled]="forgotLoading">
                  <span class="button-label">
                    <span *ngIf="forgotLoading" class="login-spinner" aria-hidden="true"></span>
                    {{ forgotLoading ? 'Enviando...' : 'Enviar enlace' }}
                  </span>
                  <i *ngIf="!forgotLoading" class="bi bi-envelope-arrow-up-fill" aria-hidden="true"></i>
                </button>
              </form>
              <button type="button" class="link-btn back-link" (click)="goLogin()">Volver al inicio de sesión</button>
            </div>
            <ng-template #forgotDone>
              <p class="success-banner">{{ forgotSuccess }}</p>
              <button type="button" class="login-btn" (click)="goLogin()">
                <span class="button-label">Volver al inicio de sesión</span>
                <i class="bi bi-arrow-right" aria-hidden="true"></i>
              </button>
            </ng-template>
          </div>
        </div>

        <footer class="access-footer" *ngIf="mode !== 'reset'">
          <div class="access-footer-main">
            <span><i class="bi bi-shield-check"></i> Acceso seguro</span>
            <span>TuSala © 2026</span>
          </div>
        </footer>
      </section>

      <div class="reset-view" *ngIf="mode === 'reset'">
        <div class="starfield" aria-hidden="true">
          <span *ngFor="let s of stars" class="star"
            [style.left.%]="s.left" [style.top.%]="s.top"
            [style.width.px]="s.size" [style.height.px]="s.size"
            [style.--dx]="s.dx + 'px'" [style.--dy]="s.dy + 'px'" [style.--dur]="s.dur + 's'"></span>
        </div>
        <section class="recovery-modal" role="dialog" aria-modal="true" aria-labelledby="recovery-title" *ngIf="recoveryTokenStatus === 'checking'">
          <span class="recovery-icon"><i class="bi bi-shield-lock"></i></span>
          <h3 id="recovery-title">Verificando enlace...</h3>
          <p><span class="login-spinner" aria-hidden="true"></span> Comprobando que el enlace siga vigente.</p>
        </section>

        <!-- Corrección pedida: antes el formulario se mostraba de una vez,
             sin saber si el enlace seguía siendo válido -- un enlace ya
             usado o vencido solo se descubría al presionar "Guardar
             contraseña". Ahora se valida primero y ninguno de los dos
             estados posibles pisa al otro. -->
        <section class="recovery-modal" role="dialog" aria-modal="true" aria-labelledby="recovery-title" *ngIf="recoveryTokenStatus === 'valid'">
          <span class="recovery-icon"><i class="bi bi-shield-lock"></i></span>
          <h3 id="recovery-title">Actualiza tu contraseña</h3>
          <p>Por seguridad, crea una contraseña nueva para tu cuenta administrativa.</p>
          <label for="recovery-password">Nueva contraseña</label>
          <div class="recovery-password-shell">
            <input id="recovery-password" [type]="showRecoveryPassword ? 'text' : 'password'" [(ngModel)]="recoveryPassword" autocomplete="new-password" placeholder="6 o más caracteres" minlength="6" maxlength="200">
            <button type="button" class="password-toggle" (click)="showRecoveryPassword = !showRecoveryPassword"
              [attr.aria-label]="showRecoveryPassword ? 'Ocultar nueva contraseña' : 'Mostrar nueva contraseña'"
              [attr.aria-pressed]="showRecoveryPassword">
              <i [class]="showRecoveryPassword ? 'bi bi-eye' : 'bi bi-eye-slash'" aria-hidden="true"></i>
            </button>
          </div>
          <div class="recovery-rules"><span [class.ok]="recoveryRule()">Mínimo 6 caracteres</span></div>
          <label for="recovery-confirm">Confirmar contraseña</label>
          <div class="recovery-password-shell">
            <input id="recovery-confirm" [type]="showRecoveryConfirm ? 'text' : 'password'" [(ngModel)]="recoveryConfirm" autocomplete="new-password" placeholder="Repite tu nueva contraseña" maxlength="200">
            <button type="button" class="password-toggle" (click)="showRecoveryConfirm = !showRecoveryConfirm"
              [attr.aria-label]="showRecoveryConfirm ? 'Ocultar confirmación de contraseña' : 'Mostrar confirmación de contraseña'"
              [attr.aria-pressed]="showRecoveryConfirm">
              <i [class]="showRecoveryConfirm ? 'bi bi-eye' : 'bi bi-eye-slash'" aria-hidden="true"></i>
            </button>
          </div>
          <small *ngIf="recoveryConfirm" class="recovery-match" [class.ok]="recoveryPassword === recoveryConfirm">{{recoveryPassword === recoveryConfirm ? 'Las contraseñas coinciden.' : 'Las contraseñas todavía no coinciden.'}}</small>
          <small>Este enlace es de un solo uso y expirará automáticamente.</small>
          <p class="error-message modal-error" *ngIf="recoveryError">{{ recoveryError }}</p>
          <button type="button" class="login-btn" [disabled]="recoveryLoading || !recoveryReady()" (click)="submitRecovery()">
            <span class="button-label">
              <span *ngIf="recoveryLoading" class="login-spinner" aria-hidden="true"></span>
              {{ recoveryLoading ? 'Guardando...' : 'Guardar contraseña' }}
            </span>
          </button>
          <button type="button" class="login-btn ghost" (click)="closeRecovery()">Cancelar</button>
        </section>

        <section class="recovery-modal recovery-success-card" role="status" aria-live="polite" aria-labelledby="recovery-success-title" *ngIf="recoveryTokenStatus === 'success'">
          <span class="recovery-icon recovery-icon-success"><i class="bi bi-check2-circle"></i></span>
          <span class="recovery-kicker">CONTRASEÑA ACTUALIZADA</span>
          <h3 id="recovery-success-title">Tu nueva contraseña quedó registrada</h3>
          <p>{{ recoverySuccess || 'La contraseña se actualizó correctamente.' }} Ya puedes ingresar al panel con tu nueva contraseña.</p>
          <div class="recovery-success-note"><i class="bi bi-shield-check" aria-hidden="true"></i><span>Este enlace ya fue utilizado y no podrá abrirse nuevamente.</span></div>
          <button type="button" class="login-btn" (click)="closeRecovery()">
            <span class="button-label">Ir al inicio de sesión</span>
            <i class="bi bi-arrow-right" aria-hidden="true"></i>
          </button>
        </section>

        <!-- Enlace ya usado, vencido o inválido: nunca se ven los campos de
             contraseña -- solo un mensaje intuitivo y la salida. -->
        <section class="recovery-modal" role="dialog" aria-modal="true" aria-labelledby="recovery-title" *ngIf="recoveryTokenStatus === 'used' || recoveryTokenStatus === 'expired' || recoveryTokenStatus === 'invalid'">
          <span class="recovery-icon recovery-icon-warning"><i class="bi bi-link-45deg"></i></span>
          <h3 id="recovery-title">{{ recoveryTokenStatus === 'used' ? 'Este enlace ya fue utilizado' : recoveryTokenStatus === 'expired' ? 'Este enlace ya venció' : 'Este enlace no es válido' }}</h3>
          <p>
            {{ recoveryTokenStatus === 'used'
              ? 'La contraseña de esta cuenta ya se actualizó con este enlace. Por seguridad, cada enlace solo puede usarse una vez.'
              : recoveryTokenStatus === 'expired'
                ? 'Este enlace tenía un tiempo límite de uso y ya se cumplió.'
                : 'Este enlace no corresponde a ninguna solicitud vigente.' }}
            Solicita uno nuevo para continuar.
          </p>
          <div class="recovery-support" role="note">
            <strong>¿Necesitas ayuda?</strong>
            <span>También puedes comunicarte directamente con el equipo de TuSala.</span>
            <div class="recovery-support-actions">
              <a *ngIf="recoveryWhatsappUrl() as url" [href]="url" target="_blank" rel="noopener noreferrer"><i class="bi bi-whatsapp"></i> WhatsApp</a>
              <a [href]="recoveryMailUrl()"><i class="bi bi-envelope"></i> {{ recoverySupportEmail() }}</a>
            </div>
          </div>
          <button type="button" class="login-btn" (click)="goForgot()">
            <span class="button-label">Solicitar nuevo enlace</span>
            <i class="bi bi-arrow-right" aria-hidden="true"></i>
          </button>
          <button type="button" class="login-btn ghost" (click)="closeRecovery()">Volver al inicio de sesión</button>
        </section>

        <app-kubo *ngIf="mode === 'reset' && recoveryTokenStatus !== 'checking' && recoveryTokenStatus !== 'success'" [recommendations]="kuboRecommendations()" storageKey="login-reset-kubo"></app-kubo>
      </div>
    </main>
  `,
  styles: [`
    .recovery-rules{display:flex;flex-wrap:wrap;gap:.3rem}.recovery-rules span{padding:.25rem .4rem;color:var(--ts-text-sky-400-16);border:1px solid var(--ts-border-navy-700-10);border-radius:999px;background:var(--ts-bg-navy-850-28);font-size:.58rem;font-weight:750}.recovery-rules span.ok{color:var(--ts-text-teal-350-5);border-color:var(--ts-border-teal-500-a35);background:var(--ts-bg-teal-650-a10)}.recovery-match{color:var(--ts-text-red-200-4)}.recovery-match.ok{color:var(--ts-text-teal-350-5)}
    :host {
      display: block;
      min-height: 100vh;
      color: var(--ts-text-soft-2);
      font-family: Inter, "Plus Jakarta Sans", system-ui, sans-serif;
      background: var(--ts-bg-navy-950-2);
    }

    *, *::before, *::after { box-sizing: border-box; }
    button, input { font: inherit; }

    .login-page {
      position: relative;
      display: grid;
      grid-template-columns: minmax(0, 55fr) minmax(480px, 45fr);
      min-height: 100svh;
      overflow: hidden;
      isolation: isolate;
      background:
        linear-gradient(90deg, var(--ts-bg-navy-950-a12) 0%, var(--ts-bg-navy-950-a4) 46%, var(--ts-bg-navy-950-a42) 100%),
        var(--login-bg) center / cover no-repeat,
        var(--ts-bg-navy-950-2);
    }

    .login-page::before {
      content: '';
      position: absolute;
      inset: 0;
      z-index: -1;
      pointer-events: none;
      background:
        linear-gradient(90deg, transparent 48%, var(--ts-bg-navy-950-a18-2) 62%, var(--ts-bg-navy-950-a52) 100%),
        radial-gradient(circle at 76% 28%, var(--ts-bg-blue-450-a13), transparent 28rem);
    }

    .login-page::after {
      content: '';
      position: absolute;
      inset: auto 0 0;
      height: 24%;
      z-index: -1;
      background: linear-gradient(0deg, var(--ts-bg-navy-950-a62), transparent);
      pointer-events: none;
    }

    .ambient {
      position: absolute;
      z-index: -1;
      border: 1px solid var(--ts-border-blue-350-a10);
      border-radius: 50%;
      pointer-events: none;
    }
    .ambient-one { width: 420px; height: 420px; top: -260px; right: -150px; }
    .ambient-two { width: 340px; height: 340px; bottom: -220px; right: 16%; border-color: var(--ts-border-indigo-300-a10); }

    .particle-field {
      position: absolute;
      inset: 0;
      z-index: 4;
      overflow: hidden;
      pointer-events: none;
    }
    .particle-field span {
      position: absolute;
      width: 3px;
      height: 3px;
      border-radius: 50%;
      background: var(--ts-bg-sky-350-2);
      box-shadow: 0 0 9px var(--ts-primary);
      opacity: .35;
      animation: particle-drift 10s ease-in-out infinite alternate;
      pointer-events: none;
    }
    .particle-field span:nth-child(1) { left: 8%; top: 12%; }
    .particle-field span:nth-child(2) { left: 42%; top: 23%; animation-delay: -3s; }
    .particle-field span:nth-child(3) { left: 47%; top: 72%; animation-delay: -6s; }
    .particle-field span:nth-child(4) { right: 8%; top: 14%; animation-delay: -2s; }
    .particle-field span:nth-child(5) { right: 5%; bottom: 12%; animation-delay: -7s; }
    .particle-field span:nth-child(6) { left: 26%; bottom: 7%; animation-delay: -4s; }

    .visual-panel {
      position: relative;
      min-width: 0;
      min-height: 100vh;
      padding: clamp(32px, 4vw, 70px);
    }

    .visual-copy {
      position: absolute;
      z-index: 2;
      left: clamp(32px, 4vw, 72px);
      bottom: clamp(32px, 5vh, 64px);
      max-width: 470px;
      padding: 18px 20px;
      border: 1px solid var(--ts-border-blue-250-a18-3);
      border-radius: 16px;
      background: linear-gradient(145deg, var(--ts-bg-navy-900-a66), var(--ts-bg-navy-900-a38));
      box-shadow: 0 18px 45px var(--ts-shadow-gray-1000-a18);
      backdrop-filter: blur(14px);
      animation: fade-up .8s ease both;
    }
    .visual-kicker {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      margin-bottom: 8px;
      color: var(--ts-text-blue-250-15);
      font-size: .72rem;
      font-weight: 750;
      letter-spacing: .08em;
      text-transform: uppercase;
    }
    .visual-copy h2 { margin: 0; color: var(--ts-text-blue-0-2); font-size: clamp(1.35rem, 2vw, 2rem); line-height: 1.16; letter-spacing: -.035em; }
    .visual-copy p { margin: 9px 0 0; color: var(--ts-text-on-accent-2); font-size: .9rem; line-height: 1.55; }

    .benefit-card {
      position: absolute;
      z-index: 3;
      display: grid;
      grid-template-columns: 38px 1fr;
      gap: 11px;
      align-items: center;
      width: min(220px, 24vw);
      padding: 13px 14px;
      border: 1px solid var(--ts-border-blue-300-a32);
      border-radius: 14px;
      background: linear-gradient(145deg, var(--ts-bg-navy-850-a74), var(--ts-bg-navy-900-a54));
      box-shadow: 0 15px 35px var(--ts-shadow-gray-1000-a20), inset 0 1px var(--ts-shadow-gray-0-a4);
      backdrop-filter: blur(13px);
      animation: float-card 6s ease-in-out infinite;
      transition: transform .25s ease, border-color .25s ease, background .25s ease;
    }
    .benefit-card:hover { border-color: var(--ts-border-blue-300-a58); background: var(--ts-bg-navy-850-a80); transform: translateY(-3px); }
    .benefit-card strong { display: block; color: var(--ts-text-on-accent); font-size: .82rem; line-height: 1.25; }
    .benefit-card small { display: block; margin-top: 4px; color: var(--ts-text-on-accent-2); font-size: .68rem; line-height: 1.35; }
    .benefit-icon {
      display: grid;
      place-items: center;
      width: 38px;
      height: 38px;
      border-radius: 10px;
      color: var(--ts-text-blue-300-6);
      background: var(--ts-bg-blue-400-a12);
      box-shadow: inset 0 0 0 1px var(--ts-shadow-blue-300-a18);
      font-size: 1.05rem;
    }
    .benefit-icon.violet { color: var(--ts-text-indigo-250-3); background: var(--ts-bg-indigo-350-a12-2); }
    .benefit-icon.cyan { color: var(--ts-text-cyan-400); background: var(--ts-bg-cyan-450-a10); }
    .benefit-spaces { top: 22%; left: 5%; }
    .benefit-growth { top: 42%; right: 5%; animation-delay: -2s; }
    .benefit-security { top: 60%; left: 7%; animation-delay: -4s; }

    .access-panel {
      position: relative;
      display: grid;
      align-content: center;
      justify-items: center;
      gap: 18px;
      min-width: 0;
      min-height: 100vh;
      padding: clamp(22px, 3vw, 54px);
    }

    .login-card {
      position: relative;
      width: min(520px, 100%);
      padding: clamp(28px, 4.2vh, 44px);
      overflow: hidden;
      border: 1px solid var(--ts-border-blue-300-a34-2);
      border-radius: 24px;
      background:
        radial-gradient(circle at 0 0, var(--ts-bg-blue-450-a12), transparent 42%),
        linear-gradient(145deg, var(--ts-bg-navy-850-a88), var(--ts-bg-navy-900-a82));
      box-shadow: 0 28px 70px var(--ts-shadow-gray-1000-a35), inset 0 1px var(--ts-shadow-gray-0-a4-2);
      backdrop-filter: blur(20px) saturate(1.12);
      animation: login-enter .75s cubic-bezier(.22, .61, .36, 1) both;
    }
    .login-card::before {
      content: '';
      position: absolute;
      width: 220px;
      height: 220px;
      top: -160px;
      left: -120px;
      border-radius: 50%;
      background: var(--ts-bg-blue-450-a18);
      filter: blur(45px);
      pointer-events: none;
    }

    .login-header { position: relative; margin-bottom: 28px; text-align: center; }
    .login-logo { display:block;width:160px;max-height:58px;margin:0 auto 14px;padding:0;object-fit:contain;background:transparent;box-shadow:none; }
    .admin-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 17px;
      padding: 5px 9px;
      color: var(--ts-text-blue-200-9);
      border: 1px solid var(--ts-border-blue-350-a22);
      border-radius: 999px;
      background: var(--ts-bg-blue-550-a10);
      font-size: .67rem;
      font-weight: 700;
      letter-spacing: .04em;
      text-transform: uppercase;
    }
    .login-header h1 { margin: 0 0 7px; color: var(--ts-text); font-size: clamp(1.55rem, 2.4vw, 2rem); line-height: 1.14; letter-spacing: -.04em; }
    .login-header p { max-width: 390px; margin: 0 auto; color: var(--ts-text-sky-250-15); font-size: .86rem; line-height: 1.5; }

    .login-form { position: relative; display: grid; gap: 18px; }
    .form-group { display: grid; gap: 8px; margin: 0; }
    .form-group label { color: var(--ts-text-sky-100-6); font-size: .78rem; font-weight: 650; }
    .input-shell {
      position: relative;
      display: grid;
      grid-template-columns: 24px minmax(0, 1fr) auto;
      column-gap: 8px;
      align-items: center;
      padding: 0 9px 0 15px;
      min-height: 52px;
      border: 1px solid var(--ts-border-sky-400-a36);
      border-radius: 11px;
      background: var(--ts-bg-navy-900-a66-2);
      transition: border-color .22s ease, box-shadow .22s ease, background .22s ease, transform .22s ease;
    }
    .input-shell:focus-within {
      border-color: var(--ts-border-blue-350-2);
      background: var(--ts-bg-navy-900-a82-2);
      box-shadow: 0 0 0 3px var(--ts-shadow-blue-400-a13), 0 8px 22px var(--ts-shadow-gray-1000-a13);
      transform: translateY(-1px);
    }
    .input-shell.has-error { border-color: var(--ts-border-red-350-a68); background: var(--ts-bg-red-800-a20); }
    .input-icon { position: static; display:grid; place-items:center; width:24px; z-index: 1; color: var(--ts-text-sky-400-17); font-size: 1rem; pointer-events: none; }
    .form-control {
      grid-column: 2;
      min-width: 0;
      width: 100%;
      height: 50px;
      margin: 0;
      padding: 0 4px !important;
      color: var(--ts-text);
      border: 0;
      outline: 0;
      background: transparent;
      font-size: .86rem;
    }
    .form-control::placeholder { color: var(--ts-text-sky-450-10); opacity: 1; }
    .form-control:-webkit-autofill {
      -webkit-text-fill-color: var(--ts-text);
      box-shadow: 0 0 0 1000px var(--ts-shadow-navy-900) inset;
      transition: background-color 9999s ease-out;
    }
    .password-toggle {
      position: static;
      grid-column: 3;
      flex: 0 0 34px;
      display: grid;
      place-items: center;
      width: 34px;
      height: 34px;
      padding: 0;
      color: var(--ts-text-sky-350-18);
      border: 0;
      border-radius: 8px;
      background: transparent;
      cursor: pointer;
      transition: color .2s ease, background .2s ease;
    }
    .password-toggle:hover { color: var(--ts-text-on-accent-19); background: var(--ts-bg-blue-300-a11); }

    .access-issue { display: grid; gap: .8rem; margin: -.25rem 0 1rem; padding: 1rem; border-radius: 14px; border: 1px solid var(--ts-border-strong, rgba(148,173,207,.28)); background: var(--ts-surface, #0d213c); text-align: left; }
    .access-issue-head { display: flex; align-items: flex-start; gap: .75rem; }
    .access-issue-head > i { flex: 0 0 38px; height: 38px; display: grid; place-items: center; border-radius: 11px; color: var(--ts-text-navy-900); background: var(--ts-bg-amber-350); font-size: 1.1rem; }
    .access-issue-head strong { display: block; color: var(--ts-text, #eef5ff); font-size: .95rem; }
    .access-issue-head p { margin: .2rem 0 0; color: var(--ts-text-soft, #a9bad0); font-size: .84rem; line-height: 1.5; }
    .access-issue-actions { display: flex; flex-wrap: wrap; gap: .5rem; }
    .access-btn { display: inline-flex; align-items: center; justify-content: center; gap: .45rem; flex: 1 1 180px; min-height: 42px; padding: 0 .9rem; border-radius: 10px; border: 1px solid var(--ts-border-strong, rgba(148,173,207,.28)); color: var(--ts-text, #eef5ff); background: transparent; font-size: .85rem; font-weight: 700; text-decoration: none; }
    .access-btn:hover, .access-btn:focus-visible { border-color: var(--ts-primary, #2f7cff); outline: none; }
    .access-btn-primary { border-color: transparent; color: var(--ts-text-on-accent); background: var(--ts-bg-green-600); }
    .access-btn-primary:hover, .access-btn-primary:focus-visible { border-color: transparent; background: var(--ts-bg-green-650-2); }
    .access-issue-contacts { color: var(--ts-text-muted, #91a4bf); font-size: .78rem; overflow-wrap: anywhere; }
    .error-message {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      margin-top: -5px;
      padding: 10px 12px;
      color: var(--ts-text-red-150-8);
      border: 1px solid var(--ts-border-red-350-a27);
      border-radius: 9px;
      background: var(--ts-bg-red-350-a9);
      font-size: .76rem;
      font-weight: 600;
      line-height: 1.4;
      animation: error-in .25s ease both;
    }
    /* styles.css ya agrega un único indicador accesible con ::before. Oculta
       el ícono duplicado de la plantilla para que nunca se monten dos alertas. */
    .error-message > i { display: none; }

    .login-btn {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      min-height: 52px;
      margin-top: 3px;
      padding: 0 18px;
      overflow: hidden;
      color: var(--ts-text);
      border: 1px solid var(--ts-border-blue-250-a58);
      border-radius: 11px;
      background: linear-gradient(110deg, var(--ts-bg-blue-450-5) 0%, var(--ts-bg-blue-400-5) 52%, var(--ts-bg-indigo-400-4) 100%);
      box-shadow: 0 12px 28px var(--ts-shadow-blue-450-a26), inset 0 1px var(--ts-shadow-gray-0-a18);
      font-size: .9rem;
      font-weight: 760;
      cursor: pointer;
      transition: transform .22s ease, box-shadow .22s ease, filter .22s ease;
    }
    .login-btn::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(100deg, transparent 20%, var(--ts-bg-gray-0-a20) 48%, transparent 76%);
      transform: translateX(-120%);
      transition: transform .65s ease;
    }
    .login-btn:hover:not(:disabled) { filter: brightness(1.08); box-shadow: 0 16px 36px var(--ts-shadow-blue-450-a35); transform: translateY(-2px); }
    .login-btn:hover:not(:disabled)::before { transform: translateX(120%); }
    .login-btn:active:not(:disabled) { transform: translateY(0) scale(.995); }
    .login-btn:focus-visible, .password-toggle:focus-visible, .support-card:focus-visible { outline: 3px solid var(--ts-border-blue-300-a42-2); outline-offset: 3px; }
    .login-btn:disabled { opacity: .65; cursor: wait; }
    .button-label { display: inline-flex; align-items: center; gap: 9px; }
    .login-spinner { width: 16px; height: 16px; border: 2px solid var(--ts-border-gray-0-a35); border-top-color: var(--ts-text); border-radius: 50%; animation: spin .7s linear infinite; }

    .exclusive-note {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin: 22px 0;
      color: var(--ts-text-sky-350-19);
      font-size: .72rem;
    }
    .exclusive-note::before, .exclusive-note::after { content: ''; flex: 1; height: 1px; background: linear-gradient(90deg, transparent, var(--ts-bg-sky-350-a18)); }
    .exclusive-note::after { background: linear-gradient(90deg, var(--ts-bg-sky-350-a18), transparent); }
    .exclusive-note i { color: var(--ts-text-blue-350-2); }

    .support-card {
      display: grid;
      grid-template-columns: 40px 1fr auto;
      gap: 12px;
      align-items: center;
      width: 100%;
      min-height: 66px;
      padding: 11px 13px;
      color: var(--ts-text);
      border: 1px solid var(--ts-border-blue-400-a25-3);
      border-radius: 11px;
      background: var(--ts-bg-navy-900-a55-3);
      text-align: left;
      cursor: pointer;
      transition: border-color .22s ease, background .22s ease, transform .22s ease;
    }
    .support-card:hover { border-color: var(--ts-border-blue-300-a48); background: var(--ts-bg-navy-850-a72-2); transform: translateY(-1px); }
    .support-icon { display: grid; place-items: center; width: 40px; height: 40px; color: var(--ts-text-sky-350-20); border-radius: 10px; background: var(--ts-primary-soft-07); font-size: 1.15rem; }
    .support-copy strong, .support-copy small { display: block; }
    .support-copy strong { font-size: .78rem; }
    .support-copy small { margin-top: 3px; color: var(--ts-text-sky-350-21); font-size: .67rem; }
    .support-action { display: inline-flex; align-items: center; gap: 7px; color: var(--ts-text-blue-250-16); font-size: .67rem; white-space: nowrap; }

    .access-footer {
      display: grid;
      gap: 6px;
      width: min(520px, 100%);
      color: var(--ts-text-blue-300-a66);
      font-size: .66rem;
    }
    .access-footer-main { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
    .access-footer span { display: inline-flex; align-items: center; gap: 6px; }

    @keyframes login-enter { from { opacity: 0; transform: translateY(18px) scale(.985); } to { opacity: 1; transform: none; } }
    @keyframes fade-up { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; } }
    @keyframes float-card { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }
    @keyframes particle-drift { from { transform: translate3d(0, 0, 0); opacity: .2; } to { transform: translate3d(8px, -16px, 0); opacity: .55; } }
    @keyframes error-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
    @keyframes spin { to { transform: rotate(360deg); } }

    @media (max-width: 1180px) {
      .login-page { grid-template-columns: minmax(0, 48fr) minmax(460px, 52fr); background-position: 42% center; }
      .visual-copy { max-width: 390px; }
      .benefit-card { width: 190px; }
      .benefit-growth { right: 2%; }
    }

    @media (max-width: 900px) {
      .login-page { grid-template-columns: 36fr 64fr; background-position: 38% center; }
      .visual-panel { padding: 24px; }
      .visual-copy { left: 22px; bottom: 26px; max-width: 300px; padding: 14px; }
      .visual-copy h2 { font-size: 1.3rem; }
      .benefit-card { display: none; }
      .access-panel { padding: 28px; }
    }

    @media (max-width: 720px) {
      .login-page {
        display: block;
        min-height: 100svh;
        background:
          linear-gradient(var(--ts-bg-navy-950-a72-3), var(--ts-bg-navy-950-a86)),
          var(--login-bg) 72% center / cover no-repeat,
          var(--ts-bg-navy-950-2);
      }
      .visual-panel { display: none; }
      .access-panel { min-height: 100svh; padding: 28px 18px; align-content: safe center; overflow-y: auto; }
      .login-card { width: min(500px, 100%); }
    }

    @media (max-width: 480px) {
      .access-panel { align-content: safe center; padding: 22px 14px; }
      .login-card { padding: 26px 20px; border-radius: 20px; }
      .login-header { margin-bottom: 24px; }
      .login-logo { width: 145px; }
      .support-card { grid-template-columns: 38px 1fr; }
      .support-action { display: none; }
      .exclusive-note { font-size: .66rem; }
      .access-footer { padding: 0 4px; }
      .recovery-modal { padding: 20px 18px; border-radius: 18px; gap: 8px; }
      .recovery-icon { width: 36px; height: 36px; }
      .recovery-modal h3 { font-size: 1.1rem; }
    }

    @media (max-height: 760px) and (min-width: 721px) {
      .access-panel { padding-top: 18px; padding-bottom: 18px; }
      .login-card { padding: 24px 34px; }
      .login-header { margin-bottom: 20px; }
      .login-logo { width: 138px; margin-bottom: 9px; }
      .admin-badge { margin-bottom: 11px; }
      .login-form { gap: 13px; }
      .exclusive-note { margin: 15px 0; }
    }

    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after { scroll-behavior: auto !important; animation-duration: .01ms !important; animation-iteration-count: 1 !important; transition-duration: .01ms !important; }
    }
    .recovery-modal {
      width: min(460px, 100%);
      display: grid;
      gap: 10px;
      color: var(--ts-text-strong);
      background: linear-gradient(145deg, var(--ts-raised), var(--ts-bg-elevated));
      border: 1px solid var(--ts-divider);
      border-radius: 16px;
      padding: 24px;
      box-shadow: 0 24px 70px var(--ts-shadow-gray-1000-a45);
    }
    .recovery-modal h3 { margin: 0; color: var(--ts-text); font-size: 1.25rem; }
    .recovery-modal p { margin: 0; color: var(--ts-text-sky-250-16); font-size: .9rem; line-height: 1.45; }
    .recovery-modal > label{color:var(--ts-text-sky-100-6);font-size:.78rem;font-weight:700}
    .recovery-password-shell{display:grid;grid-template-columns:minmax(0,1fr) 42px;align-items:center;min-height:48px;border:1px solid var(--ts-border-sky-400-a36);border-radius:9px;background:var(--ts-bg-navy-900-a66-2);overflow:hidden;transition:border-color .2s ease,box-shadow .2s ease}
    .recovery-password-shell:focus-within{border-color:var(--ts-border-blue-350-2);box-shadow:0 0 0 3px var(--ts-shadow-blue-400-a13)}
    .recovery-password-shell input{min-width:0;width:100%;height:46px;padding:0 12px!important;border:0!important;outline:0!important;background:transparent!important;color:var(--ts-text)!important;box-shadow:none!important}
    .recovery-password-shell .password-toggle{grid-column:2;width:36px;height:36px;color:var(--ts-text-blue-200-9)}
    .recovery-kicker{color:var(--ts-text-teal-350-5);font-size:.65rem;font-weight:850;letter-spacing:.1em}
    .recovery-success-card{text-align:center;justify-items:center;padding:32px}
    .recovery-icon-success{color:var(--ts-text-teal-350-5);border-color:var(--ts-border-teal-500-a35);background:var(--ts-bg-teal-650-a10)}
    .recovery-success-card h3{font-size:1.45rem;max-width:360px}
    .recovery-success-card p{max-width:380px;font-size:.95rem}
    .recovery-success-note{display:flex;align-items:center;gap:.65rem;width:100%;padding:.8rem .9rem;border:1px solid var(--ts-border-teal-500-a35);border-radius:11px;background:var(--ts-bg-teal-650-a10);color:var(--ts-text-soft-2);font-size:.78rem;text-align:left}
    .recovery-success-note i{color:var(--ts-text-teal-350-5);font-size:1.1rem}
    .recovery-support{display:grid;gap:.45rem;padding:.85rem;border:1px solid var(--ts-divider);border-radius:11px;background:var(--ts-bg-navy-900-a55-3)}
    .recovery-support strong{color:var(--ts-text);font-size:.86rem}.recovery-support>span{color:var(--ts-text-sky-250-16);font-size:.76rem}
    .recovery-support-actions{display:flex;flex-wrap:wrap;gap:.45rem}.recovery-support-actions a{display:inline-flex;align-items:center;justify-content:center;gap:.4rem;flex:1 1 150px;min-height:38px;padding:.4rem .65rem;border:1px solid var(--ts-border-blue-300-a34-2);border-radius:9px;color:var(--ts-text-blue-200-9);text-decoration:none;font-size:.75rem;font-weight:750;overflow-wrap:anywhere}
    .recovery-modal label { display: grid; gap: 6px; color: var(--ts-text-sky-100-7); font-size: .85rem; font-weight: 700; }
    .recovery-modal input {
      min-height: 44px;
      padding: 0 12px;
      border: 1px solid var(--ts-divider);
      border-radius: 8px;
      background: var(--ts-scrollbar-track);
      color: var(--ts-text-soft-2);
      font: inherit;
    }
    .recovery-modal input:focus { outline: none; border-color: var(--ts-border-blue-400); box-shadow: 0 0 0 3px var(--ts-shadow-blue-400-a14); }
    .recovery-modal small { color: var(--ts-text-sky-350-22); }
    .modal-error { margin: 0; }
    .success-banner {
      margin: 0;
      padding: 10px 12px;
      border-radius: 8px;
      color: var(--ts-text-green-100);
      background: var(--ts-bg-green-500-a14);
      border: 1px solid var(--ts-border-green-500-a40);
      font-size: .84rem;
    }
    .recovery-icon {
      width: 40px;
      height: 40px;
      display: grid;
      place-items: center;
      border-radius: 50%;
      background: var(--ts-primary-soft-14);
      border: 1px solid var(--ts-border-blue-400-a40-2);
      color: var(--ts-text-blue-150-3);
    }
    .recovery-icon-warning {
      background: var(--ts-bg-red-350-a16);
      border-color: var(--ts-border-red-350-a40);
      color: var(--ts-text-red-150-9);
    }
    .login-options { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-top:-3px; }
    .remember-option { display:inline-flex; align-items:center; gap:8px; color:var(--ts-text-sky-350-21); font-size:.7rem; font-weight:700; cursor:pointer; }
    .remember-option input { position:absolute; width:1px; height:1px; opacity:0; pointer-events:none; }
    .remember-check { display:grid; place-items:center; width:18px; height:18px; flex:none; color:transparent; border:1px solid var(--ts-border-navy-650-3); border-radius:5px; background:var(--ts-bg-navy-900-5); transition:.2s ease; }
    .remember-option input:checked + .remember-check { color:#fff; border-color:var(--ts-primary); background:var(--ts-primary); }
    .remember-option input:focus-visible + .remember-check { outline:2px solid var(--ts-text-blue-250-16); outline-offset:2px; }
    .remember-check i { font-size:.65rem; line-height:1; }
    .link-btn {
      background: transparent;
      border: 0;
      padding: 4px 2px;
      cursor: pointer;
      color: var(--ts-text-blue-250-16);
      font-size: .76rem;
      font-weight: 650;
      text-decoration: underline;
      text-decoration-color: transparent;
      transition: color .2s ease, text-decoration-color .2s ease;
    }
    .link-btn:hover { color: var(--ts-text-blue-100-2); text-decoration-color: var(--ts-text-blue-250-a60); }
    .forgot-block { display: grid; gap: 14px; }
    .back-link { justify-content: flex-start; margin-top: 4px; }
    /* Bug real corregido: <app-kubo> se agregó como hijo directo de
       .reset-view (display:grid; place-items:center) -- a diferencia de
       .kd (position:fixed) DENTRO del componente, el propio
       elemento <app-kubo> no tenía ningún position/display propio, así
       que el grid lo trataba como un segundo ítem real y repartía el
       centrado entre dos filas en vez de centrar solo la tarjeta en todo
       el alto disponible -- por eso se veía corrida hacia arriba. Con
       display:contents, <app-kubo> desaparece del árbol de layout del
       grid (sus hijos, con su propio position:fixed, se siguen pintando
       igual) y la tarjeta vuelve a ser el único ítem a centrar. */
    .reset-view app-kubo { display: contents; }
    .reset-view {
      position: fixed;
      inset: 0;
      z-index: 1200;
      display: grid;
      place-items: center;
      padding: 16px;
      overflow-y: auto;
      background: radial-gradient(circle at 50% 28%, var(--ts-bg-navy-850-29), var(--ts-bg-navy-950-2) 72%);
    }
    .starfield { position: absolute; inset: 0; z-index: 0; pointer-events: none; overflow: hidden; }
    .star {
      position: absolute;
      border-radius: 50%;
      background: var(--ts-bg-blue-50-5);
      box-shadow: 0 0 6px var(--ts-shadow-blue-300-a80);
      opacity: .5;
      animation: star-drift var(--dur) linear infinite alternate;
    }
    @keyframes star-drift {
      from { transform: translate(0, 0); opacity: .2; }
      to { transform: translate(var(--dx), var(--dy)); opacity: .95; }
    }
    .reset-view .recovery-modal { position: relative; z-index: 1; }
    .login-btn.ghost {
      background: transparent;
      border: 1px solid var(--ts-border-blue-250-a40-3);
      box-shadow: none;
      color: var(--ts-text-sky-200-10);
    }
    .login-btn.ghost:hover:not(:disabled) { filter: none; background: var(--ts-bg-blue-300-a10); transform: translateY(-1px); }

    .login-btn:not(.ghost) { color: var(--ts-text-on-accent); }
  `]
})
export class LoginComponent implements OnInit {
  // Ruta relativa (sin "/" inicial): el fondo del login vive en public/ y se
  // copia a la raíz del build, así que se referencia como CSS custom
  // property fijada por [style] en vez de un url() dentro de la hoja de
  // estilos del componente — esbuild intenta resolver un url() relativo
  // dentro de una hoja de estilos como módulo en disco (falla, porque
  // public/ no forma parte del grafo de módulos), y un url() con "/" al
  // inicio ignora el base href y solo funciona si la app vive en la raíz
  // del dominio. Fijado como estilo en línea, el navegador lo resuelve
  // igual que <base href> resuelve <img src="..."> — válido tanto si la app
  // vive en la raíz como en una subcarpeta.
  protected readonly loginBgUrl = 'url(TuSala_FondoLogin.png)';
  @Output() supportRequested = new EventEmitter<void>();
  email = '';
  password = '';
  showPassword = false;
  rememberMe = false;
  loading = false;
  error = false;
  errorMessage = '';
  // Bloqueo de acceso por plan no activo o negocio desactivado (códigos del
  // backend en ownerLogin) -- muestra el recuadro de ayuda con contactos.
  accessIssue: 'SUBSCRIPTION_NOT_ACTIVE' | 'BUSINESS_DISABLED' | null = null;

  accessSupportEmail(): string {
    return this.platformContacts.snapshot?.support_email || 'soporte@tusala.net';
  }

  accessPhones(): string[] {
    return (this.platformContacts.snapshot?.contact_phones || []).filter(Boolean);
  }

  private accessMessage(): string {
    const account = this.email ? ` (cuenta: ${this.email.trim()})` : '';
    return this.accessIssue === 'SUBSCRIPTION_NOT_ACTIVE'
      ? `Hola, quiero activar o renovar el plan de mi negocio en TuSala${account}. ¿Me pueden enviar mi enlace de pago?`
      : `Hola, el acceso a mi negocio en TuSala aparece desactivado${account}. ¿Me pueden ayudar?`;
  }

  accessWhatsappUrl(): string {
    const digits = (this.accessPhones()[0] || '').replace(/\D/g, '');
    return digits ? `https://wa.me/${digits}?text=${encodeURIComponent(this.accessMessage())}` : '';
  }

  accessMailUrl(): string {
    const subject = this.accessIssue === 'SUBSCRIPTION_NOT_ACTIVE' ? 'Activar o renovar mi plan en TuSala' : 'Acceso a mi negocio en TuSala';
    return `mailto:${this.accessSupportEmail()}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(this.accessMessage())}`;
  }
  emailError = '';
  passwordError = '';
  recoveryToken = '';
  recoveryPassword = '';
  recoveryConfirm = '';
  showRecoveryPassword = false;
  showRecoveryConfirm = false;
  recoveryLoading = false;
  recoveryError = '';
  recoverySuccess = '';
  // Corrección pedida: antes se mostraba el formulario de nueva contraseña
  // de una vez, sin saber si el enlace seguía siendo válido -- un enlace ya
  // usado o vencido solo se descubría al presionar "Guardar contraseña". El
  // token se valida primero (GET público /auth/password-recovery/validate,
  // ya existente en el backend) y hasta entonces no se ve ningún campo.
  recoveryTokenStatus: 'checking' | 'valid' | 'success' | 'invalid' | 'expired' | 'used' = 'checking';
  recoveryTokenSupport: { email: string; phones: string[] } | null = null;
  mode: 'login' | 'forgot' | 'reset' | '2fa' = 'login';
  twoFactorCode = '';
  twoFactorLoading = false;
  twoFactorError = '';
  stars = Array.from({ length: 80 }, () => ({
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 1 + Math.random() * 2.2,
    dx: (Math.random() * 2 - 1) * 70,
    dy: (Math.random() * 2 - 1) * 90,
    dur: 6 + Math.random() * 10,
  }));
  forgotEmail = '';
  forgotLoading = false;
  forgotError = '';
  forgotSent = false;
  forgotSuccess = '';

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private platformContacts: PlatformContactsService,
    private notifications: NotificationService,
  ) {}

  ngOnInit(): void {
    // `force_reset_token` fue usado por las invitaciones de primer acceso;
    // `password_reset_token` por recuperaciones normales. Ambos abren el
    // mismo formulario seguro y se confirman contra el mismo endpoint.
    const token = this.route.snapshot.queryParamMap.get('password_reset_token')
      || this.route.snapshot.queryParamMap.get('force_reset_token') || '';
    if (token) {
      // Bug real reportado: si el navegador ya tenía una sesión guardada
      // (p. ej. el propio admin probando el enlace de OTRA cuenta sin haber
      // cerrado sesión antes), esa sesión vieja seguía intacta después de
      // completar el reseteo -- la contraseña de la cuenta correcta SÍ se
      // actualizaba en el servidor, pero al navegar dentro del panel se
      // seguía viendo la cuenta vieja, dando la impresión de haber entrado
      // a la cuenta equivocada. Se limpia la sesión ANTES de mostrar el
      // formulario, para que este enlace nunca conviva con una sesión ajena.
      this.authService.logout();
      this.recoveryToken = token;
      this.mode = 'reset';
      this.validateRecoveryToken(token);
    }
    // El endpoint es público (no requiere sesión) — se puede pedir desde el
    // login sin problema, así el botón de soporte usa el correo real
    // configurado en vez de uno fijo en el código.
    this.platformContacts.load().subscribe({ error: () => {} });
  }

  private validateRecoveryToken(token: string): void {
    this.recoveryTokenStatus = 'checking';
    this.recoveryTokenSupport = null;
    this.authService.validatePasswordRecoveryToken(token).subscribe({
      next: () => { this.recoveryTokenStatus = 'valid'; },
      error: (error) => {
        const body = error?.error || {};
        this.recoveryTokenStatus = body.status === 'used' || body.status === 'expired' ? body.status : 'invalid';
        this.recoveryTokenSupport = body.platformSupport || null;
      },
    });
  }

  /** Antes este botón solo emitía `supportRequested`, un evento que ningún
   * componente padre escuchaba — el clic no hacía nada. Ahora abre
   * directamente un correo al soporte configurado, igual que ya hace el
   * resto del panel (ver admin-topbar.component.ts). */
  contactSupport(): void {
    this.supportRequested.emit();
    const email = this.platformContacts.snapshot?.support_email || 'soporte@tusala.net';
    const subject = encodeURIComponent('Solicitud de soporte TuSala');
    window.location.href = `mailto:${email}?subject=${subject}`;
  }

  recoverySupportEmail(): string {
    return this.recoveryTokenSupport?.email || this.platformContacts.snapshot?.support_email || 'soporte@tusala.net';
  }

  recoveryWhatsappUrl(): string {
    const phone = this.recoveryTokenSupport?.phones?.[0] || this.platformContacts.snapshot?.contact_phones?.[0] || '';
    const digits = phone.replace(/\D/g, '');
    const text = encodeURIComponent('Hola, necesito ayuda con mi enlace para cambiar la contraseña de TuSala.');
    return digits ? `https://wa.me/${digits}?text=${text}` : '';
  }

  recoveryMailUrl(): string {
    const subject = encodeURIComponent('Ayuda con cambio de contraseña en TuSala');
    return `mailto:${this.recoverySupportEmail()}?subject=${subject}`;
  }

  // Pedido explícito: Kubo debe estar presente en la pantalla de enlace
  // caducado/usado, brindando los contactos de administración y soporte "de
  // forma accesible, como los usa el front del cliente" -- ahí
  // (app-help-widget) el contacto nunca es texto suelto: son acciones
  // reales de un toque (WhatsApp / correo), no un correo y teléfono escritos
  // dentro de una oración. Se replican esas mismas 2 acciones aquí mediante
  // actionLabel/secondaryActionLabel de Kubo. Mientras se ve el formulario
  // válido, la recomendación queda de fondo con prioridad baja (no compite
  // por atención, no se abre sola).
  kuboRecommendations(): KuboRecommendation[] {
    if (this.mode !== 'reset' || this.recoveryTokenStatus === 'checking' || this.recoveryTokenStatus === 'success') return [];
    const support = this.recoveryTokenSupport || {
      email: this.platformContacts.snapshot?.support_email || 'soporte@tusala.net',
      phones: this.platformContacts.snapshot?.contact_phones || [],
    };
    const phone = support.phones?.[0] || null;
    const openWhatsapp = () => {
      if (!phone) return;
      const digits = phone.replace(/\D/g, '');
      const text = encodeURIComponent('Hola, necesito ayuda con mi acceso a TuSala.');
      window.open(`https://wa.me/${digits}?text=${text}`, '_blank', 'noopener,noreferrer');
    };
    const openEmail = () => { window.location.href = `mailto:${support.email}`; };
    const contactActions = phone
      ? { actionLabel: 'Escribir por WhatsApp', onAction: openWhatsapp, secondaryActionLabel: 'Enviar correo', onSecondaryAction: openEmail }
      : { actionLabel: 'Enviar correo', onAction: openEmail };
    if (this.recoveryTokenStatus === 'valid') {
      return [{
        id: 'reset-valid-support',
        state: 'default',
        title: '¿Necesitas ayuda?',
        message: 'Si tienes dudas para completar este paso, contáctanos.',
        priority: 'low',
        ...contactActions,
      }];
    }
    const titles: Record<'invalid' | 'expired' | 'used', string> = {
      used: 'Este enlace ya fue utilizado',
      expired: 'Este enlace ya venció',
      invalid: 'Este enlace no es válido',
    };
    const status = this.recoveryTokenStatus as 'invalid' | 'expired' | 'used';
    return [{
      id: `reset-${status}-support`,
      state: 'warning',
      title: titles[status],
      message: 'Contacta a administración o soporte para que te envíen un enlace nuevo.',
      priority: 'high',
      ...contactActions,
    }];
  }

  // No reutiliza friendlyError() (utils/friendly-error.ts) a propósito: ese
  // util trata un 401 como "tu sesión expiró", correcto en el resto del
  // panel pero equivocado justo aquí -- un 401 al INICIAR sesión significa
  // credenciales incorrectas, no una sesión vencida. Sí reutiliza su mismo
  // filtro de jerga técnica, que aquí faltaba por completo -- un mensaje
  // crudo del backend (nombres de columna, "token"/"jwt", fragmentos de
  // SQL) se mostraba tal cual en la pantalla de login.
  private getErrorMessage(error: any, fallback: string): string {
    const payload = error?.error || {};
    const retryAfter = Number(payload.retry_after_seconds || 0);
    const raw = String(payload.msg || payload.message || error?.message || '').trim();
    const base = raw && !TECHNICAL_JARGON.test(raw) ? raw : fallback;
    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      const unit = retryAfter === 1 ? 'segundo' : 'segundos';
      return `${base} Intenta nuevamente en ${retryAfter} ${unit}.`;
    }
    return base;
  }

  onLogin(): void {
    const email = this.email.trim();
    this.emailError = !email
      ? 'El correo electrónico es obligatorio.'
      : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
        ? 'Ingresa un correo electrónico válido, por ejemplo nombre@empresa.com.'
        : '';
    this.passwordError = this.password ? '' : 'La contraseña es obligatoria.';
    if (this.emailError || this.passwordError) {
      this.error = true;
      this.errorMessage = 'Revisa los campos resaltados antes de continuar.';
      this.notifications.error(this.errorMessage);
      queueMicrotask(() => document.querySelector<HTMLElement>('.login-form .field-validation-message')?.previousElementSibling?.querySelector<HTMLElement>('input')?.focus());
      return;
    }

    const loadingStartedAt = Date.now();
    this.loading = true;
    this.error = false;
    this.errorMessage = '';
    this.accessIssue = null;

    this.authService.login(this.email, this.password, undefined, this.rememberMe).subscribe({
      next: (success) => {
        this.finishLoginAfterMinimumDuration(loadingStartedAt, () => {
          if (success) {
            // Limpiar estado persistido de tabs para que el dashboard inicie en overview
            try {
              localStorage.removeItem('dashboard_tab');
              localStorage.removeItem('dashboard_section');
              localStorage.removeItem('admin_navigation_collapsed');
            } catch {}
            this.router.navigate(['/dashboard'], { replaceUrl: true });
          } else {
            this.error = true;
            this.errorMessage = 'Email o contraseña incorrectos';
            this.notifications.error(this.errorMessage);
          }
        });
      },
      error: (error) => {
        this.finishLoginAfterMinimumDuration(loadingStartedAt, () => {
          // Si el servidor indica que la cuenta obliga a cambiar la contraseña
          // (p.ej. primer ingreso con contraseña temporal), el backend devuelve
          // 423 con { requires_password_change: true, reset_token } — en ese
          // caso abrir el flujo de restablecimiento con el token recibido para
          // que el usuario no tenga que ingresar la contraseña temporal aquí.
          const payload = error?.error || {};
          if (error?.status === 423 && payload.reset_token) {
            this.recoveryToken = String(payload.reset_token);
            this.mode = 'reset';
            this.validateRecoveryToken(this.recoveryToken);
            this.notifications.warning(payload.msg || 'Debes establecer una nueva contraseña para continuar.');
            return;
          }

          // El backend pide el código de 2FA (super_admin con verificación en
          // dos pasos activada) -- email/password ya quedaron correctos, así
          // que se guardan y se pasa a la pantalla de código en vez de
          // mostrar esto como un error de credenciales.
          if (error?.status === 400 && payload.requires2FA === true) {
            this.mode = '2fa';
            this.twoFactorCode = '';
            this.twoFactorError = '';
            return;
          }

          this.error = true;
          this.errorMessage = this.getErrorMessage(error, 'Error al iniciar sesión. Intente de nuevo.');
          if (error?.status === 403 && (payload.code === 'SUBSCRIPTION_NOT_ACTIVE' || payload.code === 'BUSINESS_DISABLED')) {
            this.accessIssue = payload.code;
            this.platformContacts.load().subscribe({ error: () => {} });
          }
          this.notifications.error(this.errorMessage);
        });
      }
    });
  }

  private finishLoginAfterMinimumDuration(startedAt: number, complete: () => void): void {
    const remaining = Math.max(0, 0 - (Date.now() - startedAt));
    setTimeout(() => {
      complete();
      this.loading = false;
    }, remaining);
  }

  submitRecovery(): void {
    this.recoveryError = '';
    this.recoverySuccess = '';
    if (!this.recoveryToken) {
      this.recoveryError = 'El token de recuperación no es válido.';
      this.notifications.error(this.recoveryError);
      return;
    }
    if (this.recoveryPassword !== this.recoveryConfirm) {
      this.recoveryError = 'La confirmación no coincide con la nueva contraseña.';
      this.notifications.error(this.recoveryError);
      return;
    }
    if (!this.recoveryRule()) {
      this.recoveryError = 'La contraseña debe tener al menos 6 caracteres.';
      this.notifications.error(this.recoveryError);
      return;
    }
    this.recoveryLoading = true;
    this.authService.confirmPasswordRecovery(this.recoveryToken, this.recoveryPassword).subscribe({
      next: (response) => {
        this.recoveryLoading = false;
        this.recoverySuccess = response?.msg || 'Contraseña actualizada correctamente.';
        this.notifications.success(this.recoverySuccess);
        this.recoveryPassword = '';
        this.recoveryConfirm = '';
        this.showRecoveryPassword = false;
        this.showRecoveryConfirm = false;
        this.recoveryToken = '';
        this.recoveryTokenStatus = 'success';
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { password_reset_token: null, force_reset_token: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      },
      error: (error) => {
        this.recoveryLoading = false;
        const body = error?.error || {};
        if (body.status === 'used' || body.status === 'expired' || body.status === 'invalid') {
          this.recoveryTokenStatus = body.status;
          this.recoveryTokenSupport = body.platformSupport || null;
          this.recoveryPassword = '';
          this.recoveryConfirm = '';
          return;
        }
        this.recoveryError = this.getErrorMessage(error, 'No fue posible actualizar la contraseña.');
        this.notifications.error(this.recoveryError);
      }
    });
  }

  goForgot(): void {
    this.mode = 'forgot';
    this.forgotError = '';
    this.forgotEmail = '';
  }

  goLogin(): void {
    this.mode = 'login';
    this.forgotSent = false;
    this.forgotError = '';
    this.forgotEmail = '';
    this.twoFactorCode = '';
    this.twoFactorError = '';
  }

  submitTwoFactor(): void {
    if (this.twoFactorCode.length !== 6) return;
    this.twoFactorLoading = true;
    this.twoFactorError = '';

    this.authService.login(this.email, this.password, this.twoFactorCode, this.rememberMe).subscribe({
      next: (success) => {
        this.twoFactorLoading = false;
        if (success) {
          try {
            localStorage.removeItem('dashboard_tab');
            localStorage.removeItem('dashboard_section');
            localStorage.removeItem('admin_navigation_collapsed');
          } catch {}
          this.router.navigate(['/dashboard'], { replaceUrl: true });
        } else {
          this.twoFactorError = 'Código inválido. Intenta de nuevo.';
        }
      },
      error: (error) => {
        this.twoFactorLoading = false;
        this.twoFactorError = this.getErrorMessage(error, 'Código inválido. Intenta de nuevo.');
        this.notifications.error(this.twoFactorError);
      },
    });
  }

  submitForgot(): void {
    this.forgotError = '';
    const email = this.forgotEmail.trim();
    if (!email) {
      this.forgotError = 'El correo electrónico es obligatorio.';
      this.notifications.error(this.forgotError);
      return;
    }
    this.forgotLoading = true;
    this.authService.requestPasswordRecovery(email).subscribe({
      next: (response) => {
        this.forgotLoading = false;
        this.forgotSent = true;
        this.forgotSuccess = response?.msg || 'Hemos enviado instrucciones y un enlace de recuperación a tu correo electrónico.';
        this.notifications.success(this.forgotSuccess);
      },
      error: (error) => {
        this.forgotLoading = false;
        this.forgotError = this.getErrorMessage(error, 'No fue posible enviar el enlace de recuperación.');
        this.notifications.error(this.forgotError);
      }
    });
  }

  closeRecovery(): void {
    this.mode = 'login';
    this.recoverySuccess = '';
    this.recoveryError = '';
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { password_reset_token: null, force_reset_token: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  recoveryRule(): boolean {
    return this.recoveryPassword.length >= 6;
  }

  recoveryReady(): boolean {
    return this.recoveryRule() && !!this.recoveryConfirm && this.recoveryPassword === this.recoveryConfirm;
  }
}
